<template>
  <div
    ref="frame"
    class="seat-grid"
    :class="{ zooming: tooSmall }"
    @pointerdown="onDown"
    @pointermove="onMove"
    @pointerup="onUp"
    @pointercancel="onCancel"
    @wheel.prevent="onWheel"
  >
    <div
      ref="canvas"
      class="canvas"
      :class="{ animate, 'show-labels': !tooSmall }"
      :style="{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }"
    >
      <!-- <div class="stage">
        PALCO
      </div> -->
      <div class="grid" :style="{ gridTemplateColumns: `${LABEL}px repeat(${grid.columns}, ${SEAT}px) ${LABEL}px` }">
        <template v-for="{ row, cells } in grid.rows" :key="row.id">
          <div class="row-label">
            {{ row.symbol }}
          </div>
          <template v-for="(seat, i) in cells" :key="seat?.id ?? `${row.id}-${i}`">
            <div v-if="seat === null" />
            <div
              v-else
              class="seat"
              :class="{ typed: typeIcon(seat) !== null, selected: isSelected(seat), pending: isPending(seat), disabled: isDisabled(seat) }"
              :style="{ backgroundColor: SEAT_COLORS[look(seat)] }"
              :data-seat="seat.id"
              :title="seat.full_label"
            >
              <v-icon v-if="isPending(seat)" size="14" class="mdi-spin">
                mdi-loading
              </v-icon>
              <v-icon v-else-if="typeIcon(seat) !== null" size="14" class="type-icon">
                {{ typeIcon(seat) }}
              </v-icon>
              <span v-else class="label">{{ seat.label }}</span>
            </div>
          </template>
          <div class="row-label">
            {{ row.symbol }}
          </div>
        </template>
      </div>
    </div>

    <div class="controls" @pointerdown.stop>
      <button
        type="button"
        aria-label="Aproximar"
        title="Aproximar"
        @click="zoomBy(ZOOM_STEP)"
      >
        <v-icon>mdi-plus</v-icon>
      </button>
      <button
        type="button"
        aria-label="Afastar"
        title="Afastar"
        @click="zoomBy(1 / ZOOM_STEP)"
      >
        <v-icon>mdi-minus</v-icon>
      </button>
      <button
        type="button"
        aria-label="Ajustar"
        title="Ajustar"
        @click="refit()"
      >
        <v-icon>mdi-fit-to-screen-outline</v-icon>
      </button>
    </div>

    <div v-if="tooSmall" class="hint">
      Toque para aproximar
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch, type Ref } from "vue";
import type Row from "@/types/Mapping/Row";
import type Seat from "@/types/Mapping/Seat";
import { SEAT_COLORS, SEAT_TYPE_ICONS, seatGrid, type SeatGrid, type SeatLook } from "@/utils/seatGrid";
import { fit, pinchOf, pinchZoom, shouldTapZoom, tapZoom, zoomAt, type Pinch, type Point, type Viewport } from "@/utils/panZoom";

interface Props {
  rows: Row[];
  /** Seats the buyer chose. */
  selected: Seat[];
  /** Ids of the seats on their way to the buyer, waiting on their hold. */
  pending?: string[];
}

const { rows, selected, pending = [] } = defineProps<Props>();

const emit = defineEmits<{
  /** A tap on a seat the buyer can choose or drop. */
  toggle: [seat: Seat];
}>();

defineOptions({
  name: "SeatGrid",
});

/** Seat width and row label width, in px at scale 1. */
const SEAT = 24;
const LABEL = 28;
/** Zoom factor of the + and − buttons. */
const ZOOM_STEP = 1.5;
/** Length of the click-based zoom animations, matches the CSS transition. */
const ZOOM_MS = 250;
/** How far (px) a finger may slide and still count as a tap. */
const TAP_SLOP = 6;
/** Zoom factor per wheel pixel. */
const WHEEL_SPEED = 0.002;

const frame = useTemplateRef<HTMLDivElement>("frame");
const canvas = useTemplateRef<HTMLDivElement>("canvas");

const view: Ref<Viewport> = ref({ scale: 1, x: 0, y: 0 });

/** On for click-based zooms only: pinch, drag and wheel follow the finger or cursor 1:1. */
const animate: Ref<boolean> = ref(false);
let animateTimer: ReturnType<typeof setTimeout> | undefined;

/** Whether the view still shows the fit, so a resize of the frame fits it again. */
let fitted = true;

const grid = computed<SeatGrid>(() => seatGrid(rows));
const seatsById = computed<Map<string, Seat>>(() => new Map(rows.flatMap(row => (row.seats ?? []).map(seat => [seat.id, seat]))));
const selectedIds = computed<Set<string>>(() => new Set(selected.map(seat => seat.id)));
const pendingIds = computed<Set<string>>(() => new Set(pending));

/** Seats are too small to hit: a tap zooms in instead of selecting. */
const tooSmall = computed<boolean>(() => shouldTapZoom(view.value, SEAT));

function isSelected(seat: Seat): boolean {
  return selectedIds.value.has(seat.id);
}

/** Only free seats can be chosen, and chosen ones dropped. */
function isDisabled(seat: Seat): boolean {
  return seat.status !== "available" && !isSelected(seat);
}

function isPending(seat: Seat): boolean {
  return pendingIds.value.has(seat.id);
}

/** A seat waiting on its hold already looks chosen, as it will be once held. */
function look(seat: Seat): SeatLook {
  return isSelected(seat) || isPending(seat) ? "selected" : seat.status;
}

function typeIcon(seat: Seat): string | null {
  return seat.type === "standard" ? null : SEAT_TYPE_ICONS[seat.type];
}

function show(next: Viewport, animated: boolean): void {
  clearTimeout(animateTimer);
  animate.value = animated;
  view.value = next;

  if (animated) {
    animateTimer = setTimeout(() => {
      animate.value = false;
    }, ZOOM_MS);
  }
}

/** The view showing the whole grid, null while the frame isn't laid out. */
function fitView(): Viewport | null {
  if (frame.value === null || canvas.value === null) return null;

  const box = { width: frame.value.clientWidth, height: frame.value.clientHeight };
  const content = { width: canvas.value.offsetWidth, height: canvas.value.offsetHeight };

  if (box.width === 0 || box.height === 0 || content.width === 0 || content.height === 0) return null;

  return fit(box, content);
}

function refit(animated = true): void {
  const next = fitView();
  if (next === null) return;

  show(next, animated);
  fitted = true;
}

function move(next: Viewport, animated = false): void {
  show(next, animated);
  fitted = false;
}

function zoomBy(factor: number): void {
  if (frame.value === null) return;

  move(zoomAt(view.value, factor, { x: frame.value.clientWidth / 2, y: frame.value.clientHeight / 2 }), true);
}

/** The pointer position relative to the frame. */
function framePoint(event: PointerEvent | WheelEvent): Point {
  const box = frame.value!.getBoundingClientRect();

  return { x: event.clientX - box.left, y: event.clientY - box.top };
}

function onWheel(event: WheelEvent): void {
  // Firefox can report the wheel in lines instead of pixels
  const pixels = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * 16 : event.deltaY;

  move(zoomAt(view.value, Math.exp(-pixels * WHEEL_SPEED), framePoint(event)));
}

/** Fingers (or the mouse) on the frame, by pointer id. */
const pointers = new Map<number, Point>();

/** The press that may end as a tap, null once a second finger joins. */
let press: { seatId: string | null; start: Point; moved: boolean } | null = null;

let pinch: Pinch | null = null;

function onDown(event: PointerEvent): void {
  const point = framePoint(event);

  frame.value!.setPointerCapture(event.pointerId);
  pointers.set(event.pointerId, point);
  show(view.value, false);

  if (pointers.size === 1) {
    const seat = (event.target as HTMLElement).closest<HTMLElement>("[data-seat]");
    press = { seatId: seat?.dataset.seat ?? null, start: point, moved: false };
    return;
  }

  press = null;
  const [a, b] = [ ...pointers.values() ];
  pinch = pinchOf(a, b);
}

function onMove(event: PointerEvent): void {
  const previous = pointers.get(event.pointerId);
  if (previous === undefined) return;

  const point = framePoint(event);
  pointers.set(event.pointerId, point);

  if (pinch !== null) {
    const [a, b] = [ ...pointers.values() ];
    const next = pinchOf(a, b);

    move(pinchZoom(view.value, pinch, next));
    pinch = next;
    return;
  }

  let from = previous;

  if (press !== null && !press.moved) {
    press.moved = Math.hypot(point.x - press.start.x, point.y - press.start.y) > TAP_SLOP;
    if (!press.moved) return;

    // The slide within the tap slop counts too, so the map stays under the finger
    from = press.start;
  }

  move({ ...view.value, x: view.value.x + point.x - from.x, y: view.value.y + point.y - from.y });
}

function onUp(event: PointerEvent): void {
  if (!pointers.delete(event.pointerId)) return;

  if (pointers.size < 2) pinch = null;
  if (pointers.size > 0) return;

  const tap = press;
  press = null;

  if (tap === null || tap.moved) return;

  if (tooSmall.value) {
    move(tapZoom(view.value, framePoint(event), SEAT), true);
    return;
  }

  const seat = tap.seatId === null ? undefined : seatsById.value.get(tap.seatId);

  if (seat !== undefined && !isDisabled(seat)) emit("toggle", seat);
}

function onCancel(event: PointerEvent): void {
  pointers.delete(event.pointerId);
  pinch = null;
  press = null;
}

// Another Section's grid, or the same one reloaded with a different size. Two sources, not one getter returning an
// array: a new array each time would count every reload as a change, and undo the buyer's zoom
watch([() => grid.value.columns, () => grid.value.rows.length], async () => {
  await nextTick();
  refit(false);
});

let observer: ResizeObserver | null = null;

onMounted(async () => {
  await nextTick();
  refit(false);

  // Keeps the fit while the page around it changes size, but never undoes the buyer's own zoom
  observer = new ResizeObserver(() => {
    if (fitted) refit(false);
  });
  observer.observe(frame.value!);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  clearTimeout(animateTimer);
});
</script>

<style scoped>
.seat-grid {
  position: relative;
  /* Half the screen: room to pan once zoomed in, while the rest of the page still scrolls */
  height: max(320px, 50vh);
  height: max(320px, 50dvh);
  overflow: hidden;
  touch-action: none;
  user-select: none;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: grab;

  &.zooming {
    cursor: zoom-in;
  }
}

.canvas {
  position: absolute;
  top: 0;
  left: 0;
  padding: 16px;
  transform-origin: 0 0;

  &.animate {
    transition: transform 250ms ease-out;
  }
}

.stage {
  margin: 0 auto 16px;
  width: 60%;
  padding: 6px;
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.3em;
  color: #fff;
  background: #555;
  border-radius: 0 0 40px 40px;
}

.grid {
  display: grid;
  gap: 4px;
}

.row-label {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #666;
}

.seat {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  overflow: hidden;
  border-radius: 50%;
  color: #fff;
  font-size: 9px;
  white-space: nowrap;

  &.typed {
    box-shadow: 0 0 0 2px #fff, 0 0 0 3px #333;
  }

  &.selected {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px #f7b500;
  }

  &.pending {
    animation: seat-pending 0.7s ease-in-out infinite alternate;
  }

  &.typed.selected {
    box-shadow: 0 0 0 2px #fff, 0 0 0 3px #333, 0 0 0 5px #f7b500;
  }

  &.disabled {
    cursor: not-allowed;
  }

  .label {
    display: none;
  }
}

.show-labels .seat .label {
  display: inline;
}

.controls {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  button {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    cursor: pointer;
  }
}

.hint {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #fff;
  background: rgba(0, 0, 0, 0.6);
  pointer-events: none;
}

@keyframes seat-pending {
  from {
    opacity: 1;
  }

  to {
    opacity: 0.45;
  }
}
</style>
